import {
    makeStyles 
} from '@mui/styles';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import ProgressBar from '../components/progressBar';
import {
    useContext, useEffect, useMemo, useState 
} from 'react';
import {
    Choice, 
    DiagnosticAnswers
} from '../config/types';
import {
    DiagnosticFormContextValue,
    FormContext, 
    MATFormContextValue
} from '../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../figmaTheme';

type Props = {
    hideProgress?: boolean,
    position: number,
    sectionKey: string,
    answerKey?: string,
    title?: string,
    subtitle?: string,
    choices?: Choice[],
    backJump?: number,
    forwardJump?: number
};

const useStyles = makeStyles(() => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    content: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    header: {
        fontWeight: TypographyWeights.MEDIUM,
        marginBottom: '8px'
    },
    title: {
    },
    subtitle: {
        marginTop: '16px'
    },
    choices: {
        marginTop: '24px',
        display: 'flex',
        flexDirection: 'column',
        gridGap: '8px',
    },
    choice: {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        minHeight: '45px',
        padding: '16px',
        cursor: 'pointer',
        transition: 'background-color ease-in-out 0.2s'
    },
    choiceRow: {
        display: 'flex',
        flexDirection: 'row',
        borderRadius: '8px',
        minHeight: '45px',
        padding: '16px',
        cursor: 'pointer',
        alignItems: 'center',
        columnGap: '16px',
        transition: 'background-color ease-in-out 0.2s'
    },
    choiceTitle: {
        fontFamily: 'Poppins',
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: '24px',
        margin: 0,
        transition: 'color ease-in-out 0.25s'
    },
    choiceSubtitle: {
        fontFamily: 'Poppins',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '20px',
        margin: 0,
        transition: 'color ease-in-out 0.25s'
    },
}));

export const MultipleChoiceScreen = ({
    hideProgress = false,
    position,
    sectionKey,
    answerKey: initialAnswerKey,
    title: initialTitle,
    subtitle,
    choices: initialChoices,
    backJump = 1,
    forwardJump
}: Props): JSX.Element => {
    const formContext = useContext(FormContext);
    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === sectionKey);
    }, [sectionKey, formContext?.sections]);
    const classes = useStyles();
    const theme = useTheme();
    const answerKey = useMemo(() => {
        if (section?.key === 'challenge') {
            return `${section?.challengeIdentifier}-challenge_identifier`;
        }
        return initialAnswerKey;
    }, [section?.key, section?.challengeIdentifier, initialAnswerKey]);
    const title = useMemo(() => {
        if (section?.key === 'challenge') {
            if (section?.challengeOptions?.length > 1) {
                return `Select the <span style="color:${section?.colors.main};font-weight:${TypographyWeights.BOLD}">${section?.challengeName} Challenge</span> that best fits your goals and lifestyle.`;
            } else {
                return `First, tap the tile below to opt in the <span style="color:${section?.colors.main};font-weight:${TypographyWeights.BOLD}">${section?.challengeName} Challenge</span>!`;
            }
        }
        return initialTitle;
    }, [section?.key, section?.challengeOptions, section?.colors.main, section?.challengeName, initialTitle]);
    const choices = useMemo(() => {
        if (section?.key === 'challenge') {
            return section?.challengeOptions;
        }
        return initialChoices;
    }, [section?.key, section?.challengeOptions, initialChoices]);
    const [selected, setSelected] = useState(formContext.answers[answerKey] || '');
    const [userClicked, setUserClicked] = useState(false);
    const [showButton, setShowButton] = useState(selected);
    const overrideForwardJump = useMemo(() => {
        if (answerKey === 'concern_level') return (formContext.answers as DiagnosticAnswers)?.endGoal === 'Drink Less' ? 1 : 4;
        return forwardJump;
    }, [
        answerKey,
        (formContext.answers as DiagnosticAnswers)?.endGoal,
        forwardJump
    ]);
    const [jump, setJump] = useState(choices.find(choice => choice.key === formContext.answers[answerKey])?.jump || overrideForwardJump);

    // Handle initial answer load
    useEffect(() => {
        if (!formContext.answers[answerKey]) return;

        setSelected(formContext.answers[answerKey]);
        setJump(choices.find(choice => choice.key === formContext.answers[answerKey])?.jump || overrideForwardJump);
        setShowButton(formContext.answers[answerKey]);
    }, [formContext.answers[answerKey]]);

    // Delay transition to next screen to allow time for color change animation
    useEffect(() => {
        if (!selected || !userClicked) return;
        setTimeout(() => {
            setAnswer();
        }, 300);
        setUserClicked(false);
    }, [userClicked]);

    const onChoiceClick = (e) => {
        setUserClicked(true);
        setSelected(e.currentTarget.dataset.key);
        setJump(parseInt(e.currentTarget.dataset.jump) || overrideForwardJump || 1);
    };

    const onNextButtonClick = () => {   
        setAnswer();
        setUserClicked(false);
    };

    const setAnswer = () => {
        if (answerKey === 'endGoal') {
            (formContext as DiagnosticFormContextValue).setEndGoal({
                [answerKey]: selected 
            });
        } else if (answerKey === 'customTargets') {
            (formContext as DiagnosticFormContextValue).setCustomTargets({
                [answerKey]: selected 
            });
        } else if (answerKey === 'pronouns') {
            (formContext as DiagnosticFormContextValue).setPronouns({
                [answerKey]: selected 
            });
        } else if (answerKey === (formContext as MATFormContextValue).hasPrescriptionsId) {
            (formContext as MATFormContextValue).setPrescription(formContext as MATFormContextValue, {
                [answerKey]: selected 
            });
        } else if (answerKey === (formContext as MATFormContextValue).hasAllergiesId) {
            (formContext as MATFormContextValue).setAllergies(formContext as MATFormContextValue, {
                [answerKey]: selected 
            });
        } else {
            formContext.setAnswers({
                [answerKey]: selected 
            });
        }
        formContext.goNext(jump);
    };

    const onBackClick = () => {
        formContext.goBack(backJump);
    };

    return (
        <CardContainer
            sectionKey={sectionKey}
            headerText={section?.key === 'challenge' ? 'Set Up Your Challenge' : null}
            goBack={onBackClick}
            goNext={showButton && onNextButtonClick}
            hasGradient
        >
            <Card>
                {!hideProgress && (
                    <ProgressBar
                        currentPosition={position}
                        sectionKey={sectionKey}
                    />
                )}
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box className={classes.content}>
                            {section?.title && (
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={classes.header}
                                    style={{
                                        color: section?.colors.main 
                                    }}
                                >
                                    {section.title}
                                </Typography>
                            )}
                            {title && (
                                <Typography
                                    variant={TypographyVariants.H3}
                                    className={classes.title}
                                    dangerouslySetInnerHTML={{
                                        __html: title 
                                    }}
                                />
                            )}
                            {subtitle && (
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.subtitle}
                                    dangerouslySetInnerHTML={{
                                        __html: subtitle 
                                    }}
                                />
                            )}
                            <Box className={classes.choices}>
                                {choices.map(choice => (
                                    <Box
                                        key={choice.key}
                                        data-key={choice.key}
                                        data-jump={choice.jump}
                                        className={choice?.orientation === 'horizontal' ? classes.choiceRow : classes.choice}
                                        style={{
                                            backgroundColor: choice.key === selected ? section?.colors.main : section?.colors.choice 
                                        }}
                                        onClick={onChoiceClick}
                                    >
                                        {choice?.icon && (choice?.icon)}
                                        <Box>
                                            {choice?.title && (
                                                <Typography
                                                    variant={TypographyVariants.H5}
                                                    className={classes.choiceTitle}
                                                    style={{
                                                        color: choice.key === selected ? theme.palette.white : section?.colors.choiceText 
                                                    }}
                                                >
                                                    {choice.title}
                                                </Typography>
                                            )}
                                            {choice?.subtitle && (
                                                <Typography
                                                    variant={TypographyVariants.H6}
                                                    className={classes.choiceSubtitle}
                                                    style={{
                                                        color: choice.key === selected ? theme.palette.white : section?.colors.choiceText 
                                                    }}
                                                >
                                                    {choice.subtitle}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Slide>
                    <Box
                        style={{
                            height: '500px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default MultipleChoiceScreen;
